import { generatePath } from 'react-router-dom';

export const REDIRECT_URL = 'redirectURL';

export function joinPath(segments: string[], isAbsolute = true, separator = '/') {
  const relativePath = segments.join(separator);
  return isAbsolute ? '/'.concat(relativePath) : relativePath;
}

const segments = {
  home: 'home',
  user: 'user',
  webAnalytics: 'web-analytics',
  recommendations: 'recommendations',
  greensights: 'greensights',
  chatbots: 'chatbots',
  chattertonTest: 'chattertonTest',
  collections: 'collections',
  pipelines: 'pipelines',
  leadgeneration: 'lead-generation',
  speak2me: 'speak2me',
  newsletters: 'newsletters',
  dataManager: 'data-manager',
  tenders: 'tenders',
};

export const patterns = {
  home: joinPath([':moduleName', segments.home]),
  user: joinPath([segments.user, ':userId']),
  product: {
    settings: joinPath([':productName', 'settings']),
  },
};

export const sitemap = {
  root: joinPath([]),
  login: joinPath(['oauth2', 'authorization', 'oidc']),
  register: joinPath(['api', 'register']),
  logout: joinPath(['logout']),
  recommendationAlgorithms: joinPath(['algorithms']),
  // error: generatePath(['error']),

  // Global application links
  home: joinPath([segments.home]),
  welcome: joinPath(['welcome']),
  account: joinPath(['account']),
  user: joinPath([segments.user]),
  applications: joinPath(['applications']),
  error: joinPath(['error']),

  'web-analytics': {
    home: generatePath(patterns.home, { moduleName: segments.webAnalytics }),
    settings: joinPath([segments.webAnalytics, 'settings']),
    audience: joinPath([segments.webAnalytics, 'audience']),
    sessions: joinPath([segments.webAnalytics, 'sessions']),
    visitor: joinPath([segments.webAnalytics, 'audience', ':userId']),
    export: joinPath([segments.webAnalytics, 'export']),
    tagManager: joinPath([segments.webAnalytics, 'tag-manager']),
  },

  recommendations: {
    home: generatePath(patterns.home, { moduleName: segments.recommendations }),
    settings: joinPath([segments.recommendations, 'settings']),
    strategies: joinPath([segments.recommendations, 'strategies']),
    corpus: joinPath([segments.recommendations, 'corpus']),
    itemsManagement: joinPath([segments.recommendations, 'items']),
    widget: joinPath([segments.recommendations, 'widget']),
  },

  greensights: {
    home: generatePath(patterns.home, { moduleName: segments.greensights }),
    settings: joinPath([segments.greensights, 'settings']),
    board: joinPath([segments.greensights, 'board']),
  },

  chatterton: generatePath(patterns.home, { moduleName: segments.chattertonTest }),

  chattertonTest: {
    home: generatePath(patterns.home, { moduleName: segments.chattertonTest }),
    welcome: joinPath([segments.chattertonTest, 'welcome']),
    sources: joinPath([segments.chattertonTest, 'sources']),
    settings: joinPath([segments.chattertonTest, 'settings']),
    preview: joinPath([segments.chattertonTest, 'preview']),
    embed: joinPath([segments.chattertonTest, 'embed']),
  },

  chatbots: {
    'chat-profiles': joinPath([segments.chatbots, 'chat-profiles']),
    newChatProfile: joinPath([segments.chatbots, 'chat-profiles', 'new']),
    chatProfileDetail: joinPath([segments.chatbots, 'chat-profiles', ':profileId']),
    editChatProfile: joinPath([segments.chatbots, 'chat-profiles', ':profileId', 'edit']),
    preview: joinPath([segments.chatbots, 'preview']),
  },

  dataManager: {
    collections: joinPath([segments.dataManager, segments.collections]),
    newCollection: joinPath([segments.dataManager, segments.collections, 'new']),
    collectionDetail: joinPath([segments.dataManager, segments.collections, ':collectionId']),
    editCollection: joinPath([segments.dataManager, segments.collections, ':collectionId', 'edit']),
    collection: joinPath([segments.dataManager, segments.collections, 'pipelines']),
    pipelines: joinPath([segments.dataManager, segments.pipelines]),
    newPipeline: joinPath([segments.dataManager, segments.collections, ':collectionId', segments.pipelines, 'new']),
    pipelineDetail: joinPath([segments.dataManager, segments.pipelines, ':pipelineId']),
    editPipeline: joinPath([segments.dataManager, segments.pipelines, ':pipelineId', 'edit']),
    pipelineRunDetail: joinPath([segments.dataManager, segments.pipelines, ':pipelineId', 'runs', ':runId']),
    sources: joinPath([segments.dataManager, 'sources']),
    source: joinPath([segments.dataManager, 'sources', 'documents']),
    datasources: joinPath([segments.dataManager, 'datasources']),
    newDatasource: joinPath([segments.dataManager, 'datasources', 'new']),
    configureDatasource: joinPath([segments.dataManager, 'datasources', 'new', ':sourceType', 'configure']),
    reviewDatasource: joinPath([segments.dataManager, 'datasources', 'new', ':sourceType', 'review']),
    datasourceDetail: joinPath([segments.dataManager, 'datasources', ':sourceId']),
    editDatasource: joinPath([segments.dataManager, 'datasources', ':sourceId', 'edit']),
  },

  speak2me: {
    transcription: joinPath([segments.speak2me, 'transcription']),
    translation: joinPath([segments.speak2me, 'translation']),
  },

  // Tenders
  tenders: {
    settings: joinPath([segments.tenders, 'settings']),
    watchCriteria: joinPath([segments.tenders, 'settings', 'watch-criteria']),
    organization: joinPath([segments.tenders, 'settings', 'organization']),
    platformAccounts: joinPath([segments.tenders, 'settings', 'platform-accounts']),
    agencies: joinPath([segments.tenders, 'settings', 'organization', 'agencies']),
    opportunities: joinPath([segments.tenders, 'opportunities']),
    // Platform logins, used later to fetch DCEs and Q&A.
    actions: joinPath([segments.tenders, 'actions']),
    importAO: joinPath([segments.tenders, 'opportunities', 'import']),
    // `:reference` is the notice reference (e.g. `26-58387`), not a UUID.
    opportunityDetail: joinPath([segments.tenders, 'opportunities', ':reference']),
  },

  newsletters: {
    newsLetters: joinPath([segments.newsletters]),
    instances: joinPath([segments.newsletters, 'instances']),
    editInbound: joinPath([segments.newsletters, 'edit-inbound']),
    editOutbound: joinPath([segments.newsletters, 'edit-outbound']),
    newInboundNewsLetter: joinPath([segments.newsletters, 'new-inbound-newsLetter']),
    newOutboundNewsLetter: joinPath([segments.newsletters, 'new-outbound-newsLetter']),
    todaysOpportunities: joinPath([segments.newsletters, 'todaysOpportunities']),
    editHtml: joinPath([segments.newsletters, 'edit-html']),
    validateBlocks: joinPath([segments.newsletters, 'validate-blocks']),
  },

  leadgeneration: {
    home: generatePath(patterns.home, { moduleName: segments.leadgeneration }),
    company: joinPath([segments.leadgeneration, 'company']),
    details: joinPath([segments.leadgeneration, 'company/details']),
    bookmarks: joinPath([segments.leadgeneration, 'company/bookmarks']),
  },

  ext: {
    docs: {
      root: '',
      webAnalytics: 'docs/category/web-analytics',
      greensights: 'docs/category/greensights',
      recommendations: 'docs/category/recommendations',
    },
    erp: {},
  },

  // Only for Hurence DEV profile
  devOnly: {
    admin: joinPath(['admin']), // Legacy administration page generated from JHipster
    muiComponentSamples: joinPath(['mui-component-samples']), // MUI component samples
  },
};

export const breadcrumbs = {
  // Grouping segments with no page of their own; the product link opens Opportunities.
  [segments.tenders]: {
    to: sitemap.tenders.opportunities,
  },
  settings: {
    to: sitemap.tenders.watchCriteria,
  },
  [segments.webAnalytics]: {
    to: sitemap['web-analytics'].home,
  },
  [segments.recommendations]: {
    to: sitemap.recommendations.home,
  },
  [segments.greensights]: {
    to: sitemap.greensights.home,
  },
};

export const getLoginUrl = () => {
  const port = location.port ? `:${location.port}` : '';

  // If you have configured multiple OIDC providers, then, you can update this URL to /login.
  // It will show a Spring Security generated login page with links to configured OIDC providers.
  return `//${location.hostname}${port}${location.pathname}oauth2/authorization/oidc`;
};

export const getBaseUrl = () => {
  if (typeof window !== 'undefined') {
    return location.protocol + '//' + location.host; // (or whatever)
  } else {
    return SERVER_API_URL;
  }
};

export function documentationUrl(path: string = '', baseUrl: string = getBaseUrl()) {
  try {
    const url = new URL(baseUrl);

    // Check if the current host starts with "www." and replace it if needed
    if (url.host.startsWith('www.')) {
      url.host = `support.${url.host.slice(4)}`;
    } else {
      url.host = `support.${url.host}`;
    }

    return joinPath([url.origin, path], url.protocol == null);
  } catch (error) {
    // Handle invalid URLs
    console.error('Error parsing the base URL:', error);
    return baseUrl;
  }
}

export function registerUrl(path: string, baseUrl: string = getBaseUrl()) {
  return joinPath([sitemap.register, 'redirect_uri=' + baseUrl + path], false, '?');
}

// login: '/oauth2/authorization/oidc',
// logout: '/logout',
// admin: '/admin',
// muiComponentSamples: '/mui-component-samples',
// welcome: '/welcome',
// account: '/account',
// profile: '/profile',
// applications: '/applications',
// setting: '/subscription/:id',
// overview: '/web-analytics/overview',
// audience: '/web-analytics/audience',
// sessions: '/web-analytics/sessions',
// visitor: '/web-analytics/visitor',
// reco: '/recommendations/inventories',
// greensights: '/greensights/board',
// moduleRoot: moduleName => `/home/${moduleName}`,
// recoStrategies: `/recommendations/strategies`,
